/**
 * <product-card-variants>
 *
 * Lightweight, per-card variant switcher. Reads the variants JSON embedded by
 * snippets/pcard-variant-picker.liquid and, on option change, swaps the card
 * image, updates the price, re-points the quick-add form at the selected
 * variant and toggles the add button's availability — all client-side.
 *
 * Being a custom element, it re-initialises automatically when cards are
 * swapped in via innerHTML (related products / recommendations).
 */
if (!customElements.get('product-card-variants')) {
  customElements.define(
    'product-card-variants',
    class ProductCardVariants extends HTMLElement {
      connectedCallback() {
        this.variants = this.getVariantData();
        if (!this.variants.length) return;

        // Root token match — .product-card (card/metro/list/related) or the
        // overlay card's .product-card--overlay. Avoids matching inner
        // elements like .product-card__text via substring.
        this.card = this.closest('.product-card, .product-card--overlay');
        if (!this.card) return;

        this.onChange = this.onChange.bind(this);
        this.addEventListener('change', this.onChange);

        // Sync the trade-gated state for the initially-selected variant (if any)
        // without firing a full onChange — that would wrongly disable the add
        // button when no radio is checked yet.
        const initial = this.getMatchingVariant(this.selectedOptions);
        if (initial) this.updateB2b(initial);
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onChange);
      }

      getVariantData() {
        const script = this.querySelector('script[data-pcard-variants]');
        if (!script) return [];
        try {
          return JSON.parse(script.textContent) || [];
        } catch (e) {
          return [];
        }
      }

      get selectedOptions() {
        const options = [];
        this.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
          const position = parseInt(input.dataset.optionPosition, 10);
          options[position] = input.value;
        });
        return options;
      }

      getMatchingVariant(selected) {
        return this.variants.find(
          (variant) =>
            variant.options.length === selected.length &&
            variant.options.every((value, index) => value === selected[index])
        );
      }

      onChange() {
        const variant = this.getMatchingVariant(this.selectedOptions);
        this.updateImage(variant);
        this.updatePrice(variant);
        this.updateForm(variant);
        this.updateLinks(variant);
        this.updateB2b(variant);
      }

      // Toggle the trade-gated state: when a pro-only (B2B) variant is selected,
      // the card CSS hides the price + quick-add and reveals the trade-access
      // button rendered by snippets/card-product.liquid.
      updateB2b(variant) {
        this.card.classList.toggle('product-card--variant-gated', !!(variant && variant.proOnly));
      }

      updateImage(variant) {
        if (!variant || !variant.image) return;
        const img =
          this.card.querySelector('.product-card__image--main img') ||
          this.card.querySelector('.pcard__thumbnail img') ||
          this.card.querySelector('.pcard__media img');
        if (!img) return;
        img.src = variant.image.src;
        if (variant.image.srcset) {
          img.srcset = variant.image.srcset;
          if (img.hasAttribute('data-srcset')) img.setAttribute('data-srcset', variant.image.srcset);
        }
        if (img.hasAttribute('data-src')) img.setAttribute('data-src', variant.image.src);
        if (variant.image.alt) img.alt = variant.image.alt;
      }

      updatePrice(variant) {
        if (!variant) return;

        // Standard shared price snippet (.f-price)
        const priceEl = this.card.querySelector('.f-price');
        if (priceEl) {
          priceEl.classList.toggle('f-price--on-sale', !!variant.onSale);
          priceEl.classList.toggle('f-price--sold-out', !variant.available);
          this.setText(priceEl, '.f-price__regular .f-price-item--regular > span', variant.price);
          this.setText(priceEl, '.f-price__sale .f-price-item--sale > span', variant.price);
          if (variant.compareAt) this.setText(priceEl, '.f-price__sale s span', variant.compareAt);
        }

        // Related-products inline price (.product-card__price)
        const relPrice = this.card.querySelector('.product-card__price');
        if (relPrice) {
          let html = '';
          if (variant.onSale && variant.compareAt) {
            html += `<s class="product-card__price-compare">${variant.compareAt}</s> `;
          }
          html += `<span class="product-card__price-current">${variant.price}</span>`;
          relPrice.innerHTML = html;
        }
      }

      updateForm(variant) {
        const idInput = this.card.querySelector('.product-variant-id, input[name="id"]');
        // The submit button carries name="add" in every card variant
        // (.product-card__add, .product-card__atc, .pcard__atc, …).
        const addButton = this.card.querySelector('button[name="add"]');

        const soldOut = !variant || !variant.available;
        // A pro-only (trade) variant can't be added to cart by a D2C shopper —
        // the trade-access button replaces the buy UI (see updateB2b).
        const blocked = soldOut || !!(variant && variant.proOnly);

        if (idInput && variant) {
          idInput.value = variant.id;
          idInput.disabled = blocked;
        }

        if (addButton) {
          addButton.disabled = blocked;
        }

        this.card.classList.toggle('product-card--soldout', soldOut);
      }

      updateLinks(variant) {
        if (!variant || !variant.url) return;
        this.card
          .querySelectorAll(
            'a.product-card__image-link, h3.product-card__title a, a.reversed-link, a.pcard__link'
          )
          .forEach((link) => {
            link.href = variant.url;
          });
      }

      setText(root, selector, value) {
        if (value == null) return;
        const el = root.querySelector(selector);
        if (el) el.textContent = value;
      }
    }
  );
}
